import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ChurchMap from '../components/ChurchMap';
import { useLanguage } from '../context/LanguageContext';
import { sendContactMessage } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { MapPin, Phone, Mail, Heart, Send, CheckCircle, User, AtSign, Church, Calendar, MessageSquare, Edit2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import TranslatedContent from '../components/TranslatedContent';
import usePageBanner from '../hooks/usePageBanner';
import { AutoTranslate } from '../hooks/useAutoTranslate';

const RECAPTCHA_SITE_KEY = '6LdIjVAsAAAAANn94t3x-PgqpKP2ESWhwD8nImst';

const ContactPage = () => {
  const { t, language, isRTL } = useLanguage();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    church: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('contact', language);

  // Show reCAPTCHA badge on this page
  useEffect(() => {
    document.body.classList.add('show-recaptcha');
    return () => {
      document.body.classList.remove('show-recaptcha');
    };
  }, []);

  // Church display names for confirmation dialog
  const churchNames = {
    'karlskrona': 'Karlskrona - Vår Fru av Fatima kyrka',
    'karlshamn': 'Karlshamn - S:t Bonifatius kyrka',
    'olofstrom': 'Olofström - S:t Antonius kyrka',
    '': t('noChurchSelected') || 'Ingen kyrka vald'
  };

  // Scroll to hash section when page loads
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  // Show confirmation dialog before sending
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setShowConfirmDialog(true);
  };

  // Get reCAPTCHA token
  const getRecaptchaToken = async () => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        window.grecaptcha.enterprise.ready(async () => {
          try {
            const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: 'CONTACT_FORM' });
            resolve(token);
          } catch (error) {
            console.error('reCAPTCHA error:', error);
            reject(error);
          }
        });
      } else {
        reject(new Error('reCAPTCHA not loaded'));
      }
    });
  };

  // Actually send the message after confirmation
  const handleConfirmedSubmit = async () => {
    setSubmitting(true);
    setShowConfirmDialog(false);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken();
      
      // Send message with reCAPTCHA token
      await sendContactMessage({ ...formData, recaptchaToken });
      toast.success(t('messageSent'));
      setFormData({ name: '', email: '', church: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      if (error.message === 'reCAPTCHA not loaded') {
        toast.error(t('recaptchaError') || 'Säkerhetsverifiering misslyckades. Ladda om sidan och försök igen.');
      } else {
        toast.error(t('messageError') || 'Kunde inte skicka meddelandet. Försök igen.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const churches = [
    {
      id: 'karlskrona',
      name: 'Vår Fru av Fatima kyrka',
      location: 'Karlskrona',
      address: 'Södra Kungsgatan 1, 371 31 Karlskrona',
      phone: '070-649 91 43',
      email: 'karlskrona@katolskakyrkan.se',
      coordinates: [56.1612, 15.5869],
      mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=Södra+Kungsgatan+1,+371+31+Karlskrona,+Sweden',
      secretary: { sv: 'Sekr. Joanna' },
      secretaryPhone: '076-201 77 67',
      pastor: 'Wojciech Waligórski',
      pastorPhone: '070-649 91 43',
      expeditionHours: { sv: 'Expeditionstider och telefontider: Tisdagar, Onsdagar och Torsdagar kl.08.30-10.30.' },
      biktOchBon: [
        { title: 'priestConfession', description: 'orByAgreement', useTranslation: true },
        { title: 'Utställning av det allra Heligaste Sakramentet på torsdagar mellan', description: 'kl. 16.30 – 17.30.' },
        { title: 'Tillbedjan och välsignelse av det allra Heligaste Sakramentet', description: 'på fredagar mellan kl. 16.30 – 17.30.' },
        { title: 'Rosenkransen bedjes på', description: 'onsdagar kl. 11.00.\nTorsdagar och fredagar kl. 17.30.' },
      ],
    },
    {
      id: 'karlshamn',
      name: 'S:t Bonifatius kyrka',
      location: 'Karlshamn',
      address: 'Norra Fogdelyckegatan 26, 374 38 Karlshamn',
      phone: '070-649 91 43',
      email: 'karlshamn@fatimakrona.se',
      coordinates: [56.1707, 14.8616],
      mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=Norra+Fogdelyckegatan+26,+374+38+Karlshamn,+Sweden',
      biktOchBon: [
        { title: 'priestConfession', description: 'orByAgreement', useTranslation: true },
        { title: 'Tillbedjan och välsignelse av det allra Heligaste Sakramentet', description: 'på Tisdagar mellan kl. 15.15 – 18.00.' },
        { title: 'Rosenkransen bedjes på', description: 'Tisdagar kl. 17.45.' },
      ],
    },
    {
      id: 'olofstrom',
      name: 'S:t Antonius kyrka',
      location: 'Olofström',
      address: 'Bruksgatan 8, 293 38 Olofström',
      phone: '073-396 84 82',
      email: 'olofstrom@katolskakyrkan.se',
      coordinates: [56.2794, 14.5391],
      mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=Bruksgatan+8,+293+38+Olofström,+Sweden',
      pastor: 'Fr. Raphael Kurian',
      pastorTitle: 'Subsidiar',
      pastorPhone: '073-396 84 82',
      biktOchBon: [
        { title: 'priestConfession', description: 'orByAgreement', useTranslation: true },
        { title: 'Tillbedjan och välsignelse av det allra Heligaste Sakramentet', description: 'på Fredagar mellan kl. 17.30 – 18.00.' },
      ],
    },
  ];

  return (
    <div className="min-h-screen themed-page">
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage}
          alt="Contact"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle ? <AutoTranslate>{bannerTitle}</AutoTranslate> : t('contactUs')}
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              {bannerSubtitle ? <AutoTranslate>{bannerSubtitle}</AutoTranslate> : t('lookingForwardToHearFromYou')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card id="message-form" className="border-0 shadow-xl scroll-mt-24">
              <CardContent className="p-8">
                <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6">{t('sendAMessage')}</h2>
                <form onSubmit={handleFormSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('nameLabel')} *
                    </label>
                    <Input
                      id="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('yourName')}
                      data-testid="contact-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('emailLabel')} *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t('yourEmail')}
                      data-testid="contact-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="church" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('whichChurch')}
                    </label>
                    <select
                      id="church"
                      value={formData.church}
                      onChange={(e) => setFormData({ ...formData, church: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      data-testid="contact-church"
                    >
                      <option value="">{t('selectChurch')}</option>
                      <option value="karlskrona">Karlskrona - Vår Fru av Fatima kyrka</option>
                      <option value="karlshamn">Karlshamn - S:t Bonifatius kyrka</option>
                      <option value="olofstrom">Olofström - S:t Antonius kyrka</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('messageLabel')} *
                    </label>
                    <Textarea
                      id="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder={t('message')}
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold"
                    size="lg"
                    data-testid="contact-submit"
                  >
                    {submitting ? t('sending') : t('sendMessage')}
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Church Contacts */}
            <div className="space-y-6">
              {churches.map((church) => (
                <Card key={church.id} className="border-0 shadow-xl">
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">{church.name}</h3>
                    <div className="space-y-3">
                      <a 
                        href={church.mapsUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-start space-x-3 group hover:bg-gray-50 p-2 -m-2 rounded-lg transition-colors"
                        title={t('getDirections') || 'Få vägbeskrivning'}
                      >
                        <MapPin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0 group-hover:text-blue-700" />
                        <div>
                          <p className="text-blue-600 group-hover:text-blue-700 group-hover:underline">{church.address}</p>
                          <p className="text-xs text-gray-500">{t('clickForDirections') || 'Klicka för vägbeskrivning'}</p>
                        </div>
                      </a>
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        <a href={`tel:${church.phone}`} className="text-blue-600 hover:underline">
                          {church.phone}
                        </a>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        <a href={`mailto:${church.email}`} className="text-blue-600 hover:underline">
                          {church.email}
                        </a>
                      </div>
                      
                      {church.secretary && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm font-semibold text-gray-900">{t('parishOffice')}</p>
                          <TranslatedContent
                            content={church.secretary}
                            as="p"
                            className="text-sm text-gray-700"
                          />
                          {church.secretaryPhone && (
                            <p className="text-sm text-gray-600">
                              <Phone className="w-3 h-3 inline mr-1" />
                              Telefon: {church.secretaryPhone}
                            </p>
                          )}
                          {church.expeditionHours && (
                            <TranslatedContent
                              content={church.expeditionHours}
                              as="p"
                              className="text-sm text-gray-600 mt-1"
                            />
                          )}
                        </div>
                      )}
                      
                      {church.pastor && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg">
                          <p className="text-sm font-semibold text-gray-900">{church.pastorTitle || t('churchPastor')}</p>
                          <p className="text-sm text-gray-700">{church.pastor}</p>
                          {church.pastorPhone && (
                            <p className="text-sm text-gray-600">{t('phone')}: {church.pastorPhone}</p>
                          )}
                        </div>
                      )}

                      {/* Bikt och Bön section for this church */}
                      {church.biktOchBon && church.biktOchBon.length > 0 && (
                        <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                            {t('confessionAndPrayer')}
                          </h4>
                          <div className="space-y-2">
                            {church.biktOchBon.map((item, idx) => (
                              <div key={`${church.id}-bikt-${item.title?.slice(0,20) || idx}`} className="p-2 bg-white rounded text-sm">
                                <p className="font-medium text-gray-900">
                                  {item.useTranslation ? t(item.title) : <AutoTranslate>{item.title}</AutoTranslate>}
                                </p>
                                <p className="text-gray-600 whitespace-pre-line">
                                  {item.useTranslation ? t(item.description) : <AutoTranslate>{item.description}</AutoTranslate>}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Announcements Button for this church */}
                      <div className="mt-4">
                        <Link 
                          to={`/events?church=${church.id}`}
                          className="inline-flex items-center justify-center w-full px-4 py-3 bg-amber-100 hover:bg-amber-200 text-amber-800 font-medium rounded-lg transition-colors"
                          data-testid={`announcements-button-${church.id}`}
                        >
                          <Megaphone className="w-5 h-5 mr-2" />
                          {t('viewAnnouncements') || 'Se meddelanden'}
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Donation Card */}
              <Card id="donate" className="border-0 shadow-xl bg-gradient-to-br from-yellow-50 to-white scroll-mt-24">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Heart className="w-8 h-8 text-red-500" />
                    <h3 className="text-2xl font-serif font-bold text-gray-900">{t('donate')}</h3>
                  </div>
                  <p className="text-gray-700 mb-6">
                    {t('supportParish')}
                  </p>
                  
                  {/* Swish Section */}
                  <div className="bg-white p-6 rounded-lg border-2 border-yellow-400 mb-4">
                    <div className="flex flex-col items-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_758a8f60-e974-403f-a992-2e40a1d6338f/artifacts/x6oy81k0_swish-QR-small-198x300.png"
                        alt="Swish QR Code"
                        className="w-48 h-auto mb-3"
                      />
                      <p className="text-sm text-gray-600 mb-1">{t('swishNumber')}</p>
                      <p className="text-2xl font-bold text-yellow-600">123-624 89 75</p>
                      <p className="text-xs text-gray-500 mt-2 text-center">Vår fru av Fatima</p>
                    </div>
                  </div>

                  {/* Bankgiro Section */}
                  <div className="bg-white p-6 rounded-lg border-2 border-green-400">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-3">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{t('bankgiro')}</p>
                      <p className="text-2xl font-bold text-green-600">815-6085</p>
                      <p className="text-xs text-gray-500 mt-2">{t('forBankTransfers')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Map with all 3 Churches */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-8 text-center">{t('findUs')}</h2>
          
          {/* Custom Leaflet Map showing only our 3 churches */}
          <ChurchMap height="500px" />
          
          {/* Church Location Cards below map */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {churches.map((church) => (
              <a
                key={`map-${church.id}`}
                href={church.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <Card className="border-0 shadow-xl card-hover hover:shadow-xl transition-shadow cursor-pointer group-hover:border-yellow-400 border-2 border-transparent">
                  <CardContent className="p-4 flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0 group-hover:bg-yellow-200 transition-colors">
                      <MapPin className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 group-hover:text-yellow-600 transition-colors">{church.name}</h4>
                      <p className="text-sm text-gray-600">{church.address}</p>
                      <p className="text-xs text-blue-600 mt-1 group-hover:underline">{t('getDirections') || 'Få vägbeskrivning'} →</p>
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="sm:max-w-md" data-testid="confirm-message-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-xl">
              <CheckCircle className="w-6 h-6 text-yellow-500" />
              {t('confirmMessage') || 'Bekräfta ditt meddelande'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('reviewMessageBeforeSending') || 'Granska ditt meddelande innan du skickar'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <User className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-500">{t('nameLabel')}</p>
                <p className="text-gray-900">{formData.name}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <AtSign className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-500">{t('emailLabel')}</p>
                <p className="text-gray-900">{formData.email}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Church className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-500">{t('whichChurch')}</p>
                <p className="text-gray-900">{churchNames[formData.church]}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Mail className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-500">{t('messageLabel')}</p>
                <p className="text-gray-900 whitespace-pre-wrap max-h-32 overflow-y-auto">{formData.message}</p>
              </div>
            </div>
          </div>

          <AlertDialogFooter className="flex gap-2 sm:gap-2">
            <AlertDialogCancel
              onClick={() => setShowConfirmDialog(false)}
              data-testid="confirm-cancel-btn"
            >
              {t('editMessage') || 'Redigera'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedSubmit}
              disabled={submitting}
              className="bg-yellow-500 hover:bg-yellow-600 text-gray-900"
              data-testid="confirm-send-btn"
            >
              {submitting ? t('sending') : t('confirmAndSend') || 'Bekräfta & Skicka'}
              <Send className="ml-2 w-4 h-4" />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
};

export default ContactPage;
